import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Switch, TextField, IconButton, FormControlLabel, Paper, Divider, Avatar, InputAdornment, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';

// Importamos la función de envío manual junto con las demás
import { getAiMessages, toggleConversationStatus, sendManualMessage } from '../../../../controllers/company/ai/AiChatController';
import { formatWhatsAppMessage } from '../../../../controllers/AuxController';

export default function ChatWindow({ chat }) {
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isHumanActive, setIsHumanActive] = useState(chat.status === 'human_active');
    const [inputValue, setInputValue] = useState('');
    const [messageSearch, setMessageSearch] = useState('');
    const [isSending, setIsSending] = useState(false);

    // Referencia para controlar el auto-scroll automático estilo WhatsApp
    const messagesEndRef = useRef(null);

    // Sincronizar el estado local cuando cambia el chat seleccionado
    useEffect(() => {
        setIsHumanActive(chat.status === 'human_active');
        setMessageSearch(''); 
        setInputValue('');
    }, [chat]);

    // Motor de Polling de Mensajes (Cada 5 segundos de forma silenciosa)
    useEffect(() => {
        if (!chat?.id) return;

        fetchMessages(false); // Carga inicial con Spinner

        const intervalId = setInterval(() => {
            fetchMessages(true); // Peticiones silenciosas de fondo
        }, 5000);

        return () => clearInterval(intervalId);
    }, [chat]);

    // Efecto encargado de tirar el scroll hacia abajo al recibir data
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchMessages = async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const response = await getAiMessages(chat.id);
            if (response && response.data) {
                setMessages(response.data.messages || response.data);
            }
        } catch (error) {
            console.error("Error cargando los mensajes:", error);
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    };

    const changeStatus = async () => {
        try {
            await toggleConversationStatus(chat.id);
        } catch (error) {
            console.error("Error actualizando el estado del Agente:", error);
        }
    };

    // NUEVO: Función para enviar el mensaje manual
    const handleSendMessage = async () => {
        const messageText = inputValue.trim();
        if (!messageText || !isHumanActive || isSending) return;

        setIsSending(true);
        // Limpiamos el input de inmediato para mejor UX
        setInputValue('');

        try {
            await sendManualMessage(chat.id, messageText);
            // Recargamos silenciosamente los mensajes para ver el nuestro en pantalla
            await fetchMessages(true);
            scrollToBottom();
        } catch (error) {
            console.error("Error enviando mensaje manual:", error);
            // Si falla, le devolvemos el texto al usuario para que no lo pierda
            setInputValue(messageText);
        } finally {
            setIsSending(false);
        }
    };

    // Filtrado en tiempo real mediante la barra de búsqueda
    const filteredMessages = messages.filter(msg => 
        msg.content && msg.content.toLowerCase().includes(messageSearch.toLowerCase())
    );

    return (
        <>
            {/* Cabecera del Chat */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff', gap: 2 }}>
                <Box display="flex" alignItems="center" gap={2} minWidth="200px">
                    <Avatar sx={{ bgcolor: '#f3f4f6', color: '#9ca3af' }}><PersonIcon /></Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                            {chat.display_name || chat.phone}
                        </Typography>
                        <Typography variant="caption" color={isHumanActive ? 'warning.main' : 'success.main'} fontWeight="600">
                            {isHumanActive ? 'Intervención Manual Activa' : 'Agente Autónomo En Línea'}
                        </Typography>
                    </Box>
                </Box>

                {/* Buscador de Palabras Clave */}
                <Box flexGrow={1} maxWidth="400px">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar en el chat..."
                        value={messageSearch}
                        onChange={(e) => setMessageSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#9ca3af' }} />
                                </InputAdornment>
                            ),
                            sx: { 
                                borderRadius: 2, 
                                backgroundColor: '#f9fafb',
                                '& fieldset': { borderColor: 'transparent' },
                                '&:hover fieldset': { borderColor: '#e5e7eb' },
                                '&.Mui-focused fieldset': { borderColor: '#3b82f6' } 
                            }
                        }}
                    />
                </Box>
                
                <FormControlLabel
                    sx={{ minWidth: 'max-content', m: 0 }}
                    control={
                        <Switch 
                            checked={isHumanActive} 
                            onChange={(e) => {
                                setIsHumanActive(e.target.checked);
                                changeStatus();
                            }}
                            color="warning" 
                        />
                    }
                    label={
                        <Typography variant="body2" fontWeight="500" ml={1}>
                            {isHumanActive ? 'Reanudar Agente' : 'Tomar Control Humano'}
                        </Typography>
                    }
                    labelPlacement="start"
                />
            </Box>

            {/* Área de Burbujas de Mensajes */}
            <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={28} sx={{ color: '#111827' }} />
                    </Box>
                ) : (
                    <>
                        {filteredMessages.length === 0 && messageSearch !== '' && (
                            <Box textAlign="center" mt={4}>
                                <Typography variant="body2" color="text.secondary">
                                    No se encontraron mensajes que coincidan con "{messageSearch}"
                                </Typography>
                            </Box>
                        )}

                        {filteredMessages.map((msg) => {
                            const isUser = msg.role === 'user';
                            const isModel = msg.role === 'model';
                        
                            return (
                                <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-start' : 'flex-end' }}>
                                    <Paper 
                                        elevation={0}
                                        sx={{ 
                                            p: 1.5, 
                                            px: 2,
                                            maxWidth: '75%',
                                            backgroundColor: isUser ? '#fff' : (isModel ? '#e8f5e9' : '#fff3e0'), 
                                            border: '1px solid',
                                            borderColor: isUser ? '#e0e0e0' : (isModel ? '#c8e6c9' : '#ffe0b2'),
                                            borderRadius: 2,
                                            borderTopLeftRadius: isUser ? 0 : 2,
                                            borderTopRightRadius: !isUser ? 0 : 2,
                                        }}
                                    >
                                        {!isUser && (
                                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                {isModel ? <SmartToyIcon sx={{ fontSize: 14, color: '#2e7d32' }} /> : <PersonIcon sx={{ fontSize: 14, color: '#ef6c00' }} />}
                                                <Typography variant="caption" fontWeight="600" color={isModel ? '#2e7d32' : '#ef6c00'}>
                                                    {isModel ? 'Agente Sofi' : 'Tú (Staff)'}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Typography 
                                            variant="body2" 
                                            dangerouslySetInnerHTML={{ __html: formatWhatsAppMessage(msg.content) }} 
                                            sx={{ 
                                                '& br': { display: 'block', content: '""', marginTop: '4px' },
                                                wordBreak: 'break-word' 
                                            }}
                                        />
                                    </Paper>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                                        {msg.time || (msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                                    </Typography>
                                </Box>
                            );
                        })}
                        {/* El ancla invisible del scroll */}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Box>

            {/* Barra de Entrada de Envío */}
            <Divider />
            <Box sx={{ p: 2, backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={isHumanActive ? "Escribe un mensaje al cliente..." : "Pausa el Agente en el switch superior para responder"}
                    disabled={!isHumanActive || isSending}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        // Soporte para enviar con Enter sin recargar la página
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 8 } }}
                />
                <IconButton 
                    color="primary" 
                    disabled={!isHumanActive || !inputValue.trim() || isSending}
                    onClick={handleSendMessage}
                    sx={{ 
                        backgroundColor: (isHumanActive && inputValue.trim() && !isSending) ? '#111827' : '#e0e0e0',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#374151' },
                        '&.Mui-disabled': { backgroundColor: '#f3f4f6', color: '#9ca3af' }
                    }}
                >
                    {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" sx={{ ml: 0.5 }} />}
                </IconButton>
            </Box>
        </>
    );
}